import { useEffect, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  RefreshCcw,
  Search,
  ShieldAlert,
} from 'lucide-react';
import type { SeoIssue, SeoIssueSeverity } from '@pageboss/shared';
import { api } from '@/lib/api';
import { locationRoutes } from '@/lib/route-map';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

// =====================================================================
// SiteSeoAudit — fetches GET /api/sites/:siteId/audit and renders a
// collapsible list of per-page issues with severity chips. Sits at the
// bottom of the site detail page.
// =====================================================================

interface AuditPage {
  id: string;
  slug: string;
  title: string;
  published: boolean;
  issues: SeoIssue[];
}

interface AuditResponse {
  pages: AuditPage[];
  summary: {
    total: number;
    error: number;
    warning: number;
    info: number;
    cleanPages: number;
  };
}

interface SiteSeoAuditProps {
  siteId: string;
}

export function SiteSeoAudit({ siteId }: SiteSeoAuditProps) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const [data, setData] = useState<AuditResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<AuditResponse>(locationApi(businessId, siteId, 'seo-audit'));
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the audit.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  return (
    <section className="rounded-lg border border-border-subtle bg-card p-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold tracking-tight inline-flex items-center gap-2">
            <Search className="h-4 w-4 text-brand-600" aria-hidden />
            SEO health
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Scans every page on this site for missing meta, alt text, headlines, and other signals
            that affect how it shows up in search and on social.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={() => void load()}
          disabled={loading}
          className="h-auto shrink-0 px-2 py-1 text-xs text-text-secondary"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden />
          )}
          {loading ? 'Scanning…' : 'Re-scan'}
        </Button>
      </header>

      {error ? (
        <div className="mt-4 rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">{error}</div>
      ) : null}

      {data ? (
        <>
          <AuditSummary summary={data.summary} pageCount={data.pages.length} />
          <ul className="mt-4 flex flex-col gap-2">
            {data.pages.map((p) => (
              <li key={p.id}>
                <PageAuditRow page={p} siteId={siteId} />
              </li>
            ))}
          </ul>
        </>
      ) : !loading && !error ? (
        <p className="mt-4 text-sm text-text-tertiary">No audit data yet.</p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------
// Summary strip — counts + a friendly headline.
// ---------------------------------------------------------------------

interface AuditSummaryProps {
  summary: AuditResponse['summary'];
  pageCount: number;
}

function AuditSummary({ summary, pageCount }: AuditSummaryProps) {
  const allClean = summary.total === 0 && pageCount > 0;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      {allClean ? (
        <div className="inline-flex items-center gap-2 rounded-md bg-success-bg px-3 py-1.5 text-xs font-medium text-success-fg">
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
          Every page looks healthy
        </div>
      ) : null}
      <SummaryChip
        severity="error"
        label="errors"
        count={summary.error}
      />
      <SummaryChip
        severity="warning"
        label="warnings"
        count={summary.warning}
      />
      <SummaryChip
        severity="info"
        label="hints"
        count={summary.info}
      />
      <span className="text-xs text-text-tertiary">
        {summary.cleanPages} of {pageCount} pages clean
      </span>
    </div>
  );
}

function SummaryChip({
  severity,
  label,
  count,
}: {
  severity: SeoIssueSeverity;
  label: string;
  count: number;
}) {
  const cls = SEVERITY_CHIP[severity];
  if (count === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-surface-sunken px-2 py-1 text-xs font-medium text-text-tertiary">
        0 {label}
      </span>
    );
  }
  const Icon = SEVERITY_ICON[severity];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium',
        cls,
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {count} {label}
    </span>
  );
}

// ---------------------------------------------------------------------
// Per-page row — collapsible.
// ---------------------------------------------------------------------

interface PageAuditRowProps {
  page: AuditPage;
  siteId: string;
}

function PageAuditRow({ page, siteId }: PageAuditRowProps) {
  // Canonical tenant URLs carry the Business id; these screens are only
  // ever rendered inside one, so it comes from the router, not a prop.
  const businessId = useBusinessId() ?? '';
  const [open, setOpen] = useState(false);
  const topSeverity: SeoIssueSeverity | null =
    page.issues.find((i) => i.severity === 'error')?.severity ??
    page.issues.find((i) => i.severity === 'warning')?.severity ??
    page.issues.find((i) => i.severity === 'info')?.severity ??
    null;
  const Icon = topSeverity ? SEVERITY_ICON[topSeverity] : CheckCircle2;
  const tone = topSeverity ? SEVERITY_ROW_TONE[topSeverity] : 'text-success-fg';

  return (
    <div className="rounded-md border border-border-subtle bg-surface-page">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-left',
          'focus-visible:outline-2 focus-visible:outline-brand-500',
          'rounded-md',
        )}
        aria-expanded={open}
      >
        {page.issues.length > 0 ? (
          open ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-text-tertiary" aria-hidden />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-tertiary" aria-hidden />
          )
        ) : (
          <span className="w-3.5 shrink-0" aria-hidden />
        )}
        <Icon className={cn('h-4 w-4 shrink-0', tone)} aria-hidden />
        <span className="flex-1 min-w-0 truncate text-sm font-medium text-foreground">
          {page.title || page.slug}
        </span>
        <span className="shrink-0 text-xs text-text-tertiary">/{page.slug}</span>
        {page.issues.length > 0 ? (
          <span className="shrink-0 text-xs text-text-tertiary">
            {page.issues.length} issue{page.issues.length === 1 ? '' : 's'}
          </span>
        ) : (
          <span className="shrink-0 text-xs text-success-fg">clean</span>
        )}
        {!page.published ? (
          <span className="shrink-0 rounded bg-surface-sunken px-1.5 py-0.5 eyebrow">
            Draft
          </span>
        ) : null}
      </button>
      {open && page.issues.length > 0 ? (
        <div className="border-t border-border-subtle px-3 py-2">
          <ul className="flex flex-col gap-1.5">
            {page.issues.map((issue, idx) => {
              const link = issueLink(businessId, siteId, page.id, issue);
              return (
                <li
                  key={`${issue.code}-${idx}`}
                  className="flex items-start gap-2 text-sm"
                >
                  <SeverityDot severity={issue.severity} />
                  <span className="flex-1 text-text-secondary">{issue.message}</span>
                  <Link
                    to={link.href}
                    className="shrink-0 text-xs font-medium text-brand-700 hover:text-brand-800 whitespace-nowrap"
                  >
                    {link.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

// Map an audit issue to a deep-link into the page editor. Block-level
// issues (blockId set) focus the block in the canvas via the editor's
// mount-time query param consumer. Meta issues (page-wide title,
// description, OG image, etc.) open the SEO drawer. Structural issues
// (empty page, no-hero, not-published) navigate to the editor with no
// specific focus.
const SEO_META_CODES = new Set<string>([
  'missing-title',
  'title-too-long',
  'missing-description',
  'description-too-short',
  'description-too-long',
  'missing-og-image',
  'noindex',
  'duplicate-title',
]);

function issueLink(
  businessId: string,
  siteId: string,
  pageId: string,
  issue: SeoIssue,
): { href: string; label: string } {
  const base = locationRoutes.pageEdit(businessId, siteId, pageId);
  if (issue.blockId) {
    return {
      href: `${base}?focusBlock=${encodeURIComponent(issue.blockId)}`,
      label: 'Edit block →',
    };
  }
  if (SEO_META_CODES.has(issue.code)) {
    return { href: `${base}?focusSeo=true`, label: 'Open SEO settings →' };
  }
  return { href: base, label: 'Open page →' };
}

function SeverityDot({ severity }: { severity: SeoIssueSeverity }) {
  const Icon = SEVERITY_ICON[severity];
  const tone = SEVERITY_ROW_TONE[severity];
  return <Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', tone)} aria-hidden />;
}

// ---------------------------------------------------------------------
// Style maps
// ---------------------------------------------------------------------

const SEVERITY_ICON: Record<SeoIssueSeverity, typeof ShieldAlert> = {
  error: ShieldAlert,
  warning: AlertTriangle,
  info: Info,
};

const SEVERITY_CHIP: Record<SeoIssueSeverity, string> = {
  error: 'bg-danger-bg text-danger-fg',
  warning: 'bg-warning-bg text-warning-fg',
  info: 'bg-brand-50 text-brand-700',
};

const SEVERITY_ROW_TONE: Record<SeoIssueSeverity, string> = {
  error: 'text-danger-fg',
  warning: 'text-warning-fg',
  info: 'text-brand-600',
};
